/**
 * Responsive breakpoint hooks (theme-aware).
 *
 * Single source of truth for "are we below breakpoint X" checks across the app.
 * Resolves against the Mantine theme's `breakpoints` tokens (em units) rather
 * than hardcoded pixel strings, so a future breakpoint change in `theme.ts`
 * propagates everywhere automatically.
 *
 * Why not `useMediaQuery("(max-width: 768px)")` inline?
 * - Raw pixel strings silently diverge from the theme (the bug these hooks fix).
 * - `getInitialValueInEffect: false` returns the correct value on first render
 *   (this is a Vite SPA, no SSR), avoiding the desktop→mobile layout flash that
 *   the default (`true`) causes.
 */

import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

const MEDIA_QUERY_OPTIONS = { getInitialValueInEffect: false } as const;

/**
 * `true` below the `sm` breakpoint (< 48em / 768px) — i.e. phones.
 * Use for full-screen modals, stacked form rows, and the agenda calendar.
 */
export function useIsMobile(): boolean {
  const theme = useMantineTheme();
  return (
    useMediaQuery(
      `(max-width: ${theme.breakpoints.sm})`,
      false,
      MEDIA_QUERY_OPTIONS,
    ) ?? false
  );
}

/**
 * `true` below the `md` breakpoint (< 62em / 992px) — phones + tablets in
 * portrait.
 *
 * Has NO production consumer as of the 11" iPad portrait fix — `mini` in
 * `useNavbarCollapse` moved to `useIsMobile` (`sm`, matching the AppShell
 * navbar's own breakpoint, which also moved from `md` to `sm`). This hook no
 * longer matches that breakpoint and previously claimed to; kept for now as a
 * general-purpose "phones + tablet-portrait" query, not as a navbar-state
 * signal. If reaching for "does the navbar breakpoint apply", use
 * `useIsMobile` instead.
 */
export function useIsCompact(): boolean {
  const theme = useMantineTheme();
  return (
    useMediaQuery(
      `(max-width: ${theme.breakpoints.md})`,
      false,
      MEDIA_QUERY_OPTIONS,
    ) ?? false
  );
}

/**
 * `true` once the viewport has crossed Mantine's OWN internal cutover for
 * `AppShell`'s `navbar.collapsed.mobile` — not `useIsMobile`'s plain
 * `max-width: sm` (768px inclusive).
 *
 * Mantine's compiled AppShell source
 * (`assignNavbarVariables`, `getBreakpointValue(breakpoint) - 0.1`) flips its
 * OWN "is the mobile overlay collapsed" CSS at `breakpointPx - 0.1` — 767.9px
 * for `sm` — while a SEPARATE, unconditional rule forces the navbar to
 * `width: 100%` under the RAW breakpoint (768px, inclusive) whenever
 * `collapsed.mobile` is `false` (i.e. the burger overlay is open). Closing our
 * own `mobileOpened` React state on `useIsMobile` flipping false (which
 * happens at 768px, not 767.9) is 0.1px too late: at exactly 768px, our state
 * still says "open," and Mantine's raw-breakpoint override still matches,
 * reproducing the full-screen overlay a fresh load at that same width never
 * shows (a fresh load starts `collapsed.mobile=true`, which uses the EARLIER
 * 767.9 cutover and has already flipped to persistent by 768). Verified live:
 * opening the burger below 768px then resizing to exactly 768px without a
 * reload measured `navbar width: 768px` (100%) versus a fresh load's `220px`.
 *
 * Use this — not `useIsMobile` — anywhere that needs to match the EXACT
 * instant Mantine's own navbar CSS switches modes, such as closing a
 * carried-over `mobileOpened` flag before that instant is reached.
 */
export function useIsPastMobileOverlayCutover(): boolean {
  const theme = useMantineTheme();
  return (
    useMediaQuery(
      `(min-width: calc(${theme.breakpoints.sm} - 0.1px))`,
      false,
      MEDIA_QUERY_OPTIONS,
    ) ?? false
  );
}

/**
 * `true` in the tablet band — from `sm` up to (but not including) `lg`
 * (48em–75em / 768–1199px): iPad landscape AND portrait. The persistent
 * sidebar shows across this whole band (the AppShell navbar breakpoint is
 * `sm`), so use this to default it collapsed and reclaim horizontal space.
 *
 * The lower bound is `sm`, not `md`, because an 11" iPad in PORTRAIT is
 * 820–834px — inside sm..md. It has the same persistent sidebar landscape
 * does and less width to spare, so it wants the same collapsed default. When
 * this band started at `md`, portrait fell through to Mantine's
 * below-breakpoint navbar instead, which is `width: 100%` — a full-screen
 * 834x1110 takeover for ten nav links.
 */
export function useIsTablet(): boolean {
  const theme = useMantineTheme();
  return (
    useMediaQuery(
      `(min-width: ${theme.breakpoints.sm}) and (max-width: ${theme.breakpoints.lg})`,
      false,
      MEDIA_QUERY_OPTIONS,
    ) ?? false
  );
}

/**
 * `true` below the `lg` breakpoint (< 75em / 1200px) — phones AND every iPad
 * orientation, including 13" portrait (1024px), which still clears `sm..lg`
 * but never clears `lg`.
 *
 * Use for master-detail layouts that place a selectable list beside its
 * detail panel via `Grid.Col span={{ base: 12, lg: N }}` — below `lg`, both
 * columns collapse to full width and STACK, so selecting a list item pushes
 * its detail below the entire list with no scroll cue that a selection even
 * happened (found in `CareNotesTab`/`CarePlanTab`: an 11" iPad in EITHER
 * orientation, 834px or 1194px, is always below 1200px). Gate a `Drawer`
 * fallback on this hook instead: keep the side-by-side `Grid` at `lg` and up,
 * replace the detail `Grid.Col` with a `Drawer` below it.
 *
 * `CalendarTab`'s own Drawer does NOT use this hook — it opens unconditionally
 * at every width, because its `Grid.Col span={{ base: 12, lg: N }}` split is
 * unrelated content (filters vs. the calendar view), not a list+detail pair.
 */
export function useIsBelowLg(): boolean {
  const theme = useMantineTheme();
  return (
    useMediaQuery(
      `(max-width: ${theme.breakpoints.lg})`,
      false,
      MEDIA_QUERY_OPTIONS,
    ) ?? false
  );
}
